import styles from "../chat-bubble.module.css";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { EventPayload } from "@/lib/chat-store/types";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { IoTimeOutline } from "react-icons/io5";

export default function EventBubble({
  payload,
  timestamp,
  sender_id,
  receiverCompleteName,
  receiverPhotoUrl,
}: {
  payload: EventPayload;
  timestamp: number;
  sender_id: string;
  receiverCompleteName: string | undefined;
  receiverPhotoUrl: string | undefined;
}) {
  const { userData } = useUserAuthDataContext();
  const t = useTranslations("ChatsView");
  const isMine = sender_id === userData?.sub;
  const senderName =
    sender_id === userData.sub ? userData.complete_name : receiverCompleteName;
  const senderPhoto =
    sender_id === userData.sub ? userData.user_photo : receiverPhotoUrl;
  // --- Helpers ---
  const formatTime = (ts: number) => {
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 🚀 FIXED: Added the 'Z' check to ensure bulletproof timezone rendering
  const formatSlotTime = (isoString: string) => {
    if (!isoString) return "N/A";
    const safeIso = isoString.endsWith("Z") ? isoString : `${isoString}Z`;
    return new Date(safeIso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTourDate = (isoString: string) => {
    if (!isoString) return "N/A";
    const safeIso = isoString.endsWith("Z") ? isoString : `${isoString}Z`;
    return new Date(safeIso).toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const evt = payload.event;
  const isCancelled = evt?.status?.toLowerCase() === "cancelled";

  // Capitalize first letter of status for display
  const displayStatus = evt?.status
    ? evt.status.charAt(0).toUpperCase() + evt.status.slice(1).toLowerCase()
    : "Scheduled";

  return (
    <div className={`${styles.row} ${isMine ? styles.mine : styles.theirs}`}>
      <div className={styles.bubbleWrapper}>
        <div className={styles.infoAndTextWrapper}>
          <span className={styles.userInfo}>
            <span className={styles.userName}>{senderName}</span>
            <Image
              className={styles.avatar}
              src={senderPhoto || "/broken_image.webp"}
              width={24}
              height={24}
              alt="Profile photo"
            />
          </span>

          {/* Optional attached text message (e.g. "I'd like to schedule a tour...") */}
          {payload.text && (
            <span
              className={styles.text}
              style={{ marginBottom: "8px", display: "block" }}
            >
              {payload.text}
            </span>
          )}

          {/* The Rich Event Card */}
          {evt && (
            <div
              className={`${styles.eventCard} ${
                isCancelled ? styles.cancelled : ""
              }`}
              style={{ marginTop: payload.text ? "4px" : "0" }} // Adjust spacing if there's text above it
            >
              <div className={styles.tourTitle}>
                {evt.listing_title || "Property Tour"}
              </div>
              <div className={styles.tourDate}>
                <IoTimeOutline
                  style={{
                    display: "inline",
                    marginBottom: -2,
                    marginRight: 4,
                  }}
                />
                {formatTourDate(evt.slot_utc)} {t("at")}{" "}
                {formatSlotTime(evt.slot_utc)}
              </div>
              <div className={styles.tourStatus}>{displayStatus}</div>
            </div>
          )}
        </div>

        <div className={styles.messageInfo}>
          <span className={styles.time}>{formatTime(timestamp)}</span>
        </div>
      </div>
    </div>
  );
}
